import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://fateroom.eu').replace(/\/$/, '');
const DEFAULT_TITLE = 'Fate Room | Private live fortune readings';
const DEFAULT_DESCRIPTION = 'Discover independent readers, reserve a private live reading, and enter a secure Fate Room when your session begins.';

const PAGE_METADATA = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  '/find-teller': {
    title: 'Find a fortune reader | Fate Room',
    description: 'Browse independent readers by reading style, language, availability, and session price.',
  },
  '/our-services': {
    title: 'Live reading services | Fate Room',
    description: 'Explore private tarot, astrology, relationship, career, and spiritual guidance sessions on Fate Room.',
  },
  '/how-it-works': {
    title: 'How Fate Room works | Book a private reading',
    description: 'Choose a reader, reserve an available time, follow clear payment instructions, and meet in a private live room.',
  },
  '/content-library': {
    title: 'Tarot content library | Fate Room',
    description: 'Explore approachable tarot archetypes and reflective prompts for clearer reading questions.',
  },
  '/about': {
    title: 'About Fate Room',
    description: 'Learn how Fate Room supports independent readers and clients through clear, private live-reading experiences.',
  },
  '/contact': {
    title: 'Contact Fate Room support',
    description: 'Contact Fate Room for support, account help, privacy requests, and general platform questions.',
  },
  '/help': {
    title: 'Fate Room help centre',
    description: 'Practical guidance for booking, payment confirmation, joining private sessions, and managing your account.',
  },
  '/privacy': {
    title: 'Privacy policy | Fate Room',
    description: 'Read how Fate Room collects, uses, stores, and protects personal information.',
  },
  '/terms': {
    title: 'Terms and conditions | Fate Room',
    description: 'Read the terms for using Fate Room and reserving readings with independent readers.',
  },
  '/security': {
    title: 'Safety and security | Fate Room',
    description: 'Learn about secure accounts, private sessions, payment confirmations, and reporting concerns on Fate Room.',
  },
};

const PRIVATE_PATHS = [
  '/login',
  '/register',
  '/reset-password',
  '/logout',
  '/user-dashboard',
  '/teller-dashboard',
  '/onboarding/teller',
  '/admin-login',
  '/notifications',
  '/session/',
  '/session-payment/',
  '/session/edit/',
];

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
};

const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const privatePage = PRIVATE_PATHS.some((path) => pathname === path || pathname.startsWith(path));
    const metadata = PAGE_METADATA[pathname] || {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
    };
    const canonicalUrl = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

    document.title = metadata.title;
    upsertMeta('meta[name="description"]', { name: 'description', content: metadata.description });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: privatePage ? 'noindex, nofollow, noarchive' : 'index, follow',
    });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: metadata.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: metadata.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: metadata.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: metadata.description });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  }, [pathname]);

  return null;
};

export default Seo;
